import { HttpStatusCode } from "@solidjs/start";

import { Show, createSignal, createMemo, onMount } from "solid-js";
import PageHero from "@/components/page-hero";
import LinkButton from "@/components/link-button";
import { RowButtons } from "@/components/button-group";
import { Link, Check, Share2, FileText } from "lucide-solid";
import { useLocale } from "@/context/locale";
import { Button } from "@/components/ui/button";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import repos from "@/data/projects-meta.json";
import ProjectInfobox from "./ProjectInfobox";

const ALL = [...(repos.phare ?? []), ...repos.core, ...repos.edu, ...repos.legacy, ...(repos.vestigia ?? [])].filter((p) => !p.external);

export function findProject(slug) {
  return ALL.find((p) => p.slug === slug) ?? null;
}

function localDesc(project, locale) {
  return project[`desc_${locale}`] || project.desc;
}

export default function ProjectPage(props) {
  const { t, prefix, locale } = useLocale();
  const project = createMemo(() => findProject(props.slug));
  const desc = () => (project() ? localDesc(project(), locale) : "");
  const portfolioHref = `${prefix || ""}/portfolio`;
  const [copied, setCopied] = createSignal(false);
  const [canNativeShare, setCanNativeShare] = createSignal(false);
  onMount(() => {
    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    setCanNativeShare(isMobile && !!navigator.share);
  });

  function copyLink() {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function openShare(platform) {
    const url = encodeURIComponent(window.location.href);
    const title = encodeURIComponent(project()?.seo_title || project()?.name);
    const targets = {
      x: `https://x.com/intent/tweet?url=${url}&text=${title}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
    };
    window.open(targets[platform], "_blank", "noopener,noreferrer");
  }

  function nativeShare() {
    navigator.share({
      title: project()?.seo_title || project()?.name,
      text: project()?.desc || "",
      url: window.location.href,
    });
  }

  return (
   <Show
    when={project()}
    fallback={
      <section class="pt-[6%] min-h-screen bg-background flex items-center justify-center">
        <HttpStatusCode code={404} />
        <p class="text-muted-foreground">{t("portfolio.notFound")}</p>
      </section>
    }
   >
    <section class="min-h-screen">
      <PageHero
        eyebrow={project().lang}
        title={project().seo_title || project().name}
        description={project().desc ? desc() : ""}
        breadcrumb={
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href={prefix || "/"}>{t("breadcrumb.home")}</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href={portfolioHref}>{t("breadcrumb.portfolio")}</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink current>{project().name}</BreadcrumbLink>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        }
      >
        <Show when={project().seo_title}>
          <p class="-mt-2 text-lg text-muted-foreground">{project().name}</p>
        </Show>

        <RowButtons class="mt-4">
          <Show when={project().url}>
            <LinkButton href={project().url} external icon={
              <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 0C5.37 0 0 5.37 0 12c0 5.303 3.438 9.8 8.205 11.387.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0 1 12 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222 0 1.606-.015 2.896-.015 3.286 0 .322.216.694.825.576C20.565 21.795 24 17.298 24 12c0-6.63-5.37-12-12-12z"/></svg>
            }>
              GitHub
            </LinkButton>
          </Show>
          <Show when={project().homepage}>
            <LinkButton href={project().homepage} external icon={<FileText size={15} />}>{t("portfolio.documentation")}</LinkButton>
          </Show>

          <span class="text-border select-none">|</span>

          <Show
            when={canNativeShare()}
            fallback={
              <>
                <span class="text-xs uppercase tracking-widest text-foreground">{t("portfolio.share")}</span>
                <Button onClick={copyLink} variant="outline" icon={copied() ? <Check size={15} /> : <Link size={15} />}>
                  {copied() ? t("portfolio.linkCopied") : t("portfolio.copyLink")}
                </Button>
                <Button onClick={() => openShare("x")} variant="outline" icon={
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.737-8.835L1.254 2.25H8.08l4.253 5.622 5.911-5.622Zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                }>
                  {t("portfolio.shareOnX")}
                </Button>
                <Button onClick={() => openShare("linkedin")} variant="outline" icon={
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                }>
                  {t("portfolio.shareOnLinkedIn")}
                </Button>
              </>
            }
          >
            <Button onClick={nativeShare} variant="outline" icon={<Share2 size={15} />}>
              {t("portfolio.share")}
            </Button>
          </Show>
        </RowButtons>
      </PageHero>
      <div class="bg-background px-[10%] py-24">
        <ProjectInfobox project={project()} class="max-w-sm" />
      </div>
    </section>
   </Show>
  );
}
